import { useRef, type ChangeEvent, type ReactNode } from "react";
import { Button } from "./Button.js";
import type { ButtonSize } from "./Button.js";

export interface UploadButtonProps {
  /** File-picker filter, e.g. "image/jpeg,image/png,image/webp". */
  accept?: string;
  disabled?: boolean;
  size?: ButtonSize;
  icon?: ReactNode;
  /** Called once per selected file — the caller performs the actual upload. */
  onSelect: (file: File) => void | Promise<void>;
  children?: ReactNode;
}

/**
 * File picker rendered as a Button. Deliberately NOT a transport: it only opens
 * the native picker and hands the File to `onSelect` — the caller uploads through
 * the app's authenticated API client.
 */
export function UploadButton({ accept, disabled, size, icon, onSelect, children }: UploadButtonProps) {
  const ref = useRef<HTMLInputElement>(null);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void onSelect(file);
    e.target.value = "";
  };

  return (
    <>
      <input
        ref={ref}
        type="file"
        {...(accept ? { accept } : {})}
        disabled={disabled}
        className="hidden"
        onChange={onChange}
      />
      <Button
        {...(size ? { size } : {})}
        {...(icon ? { icon } : {})}
        disabled={disabled}
        onClick={() => ref.current?.click()}
      >
        {children}
      </Button>
    </>
  );
}
